/**
 * The page's four windows from one filter token (spec §2.2, §4): the period
 * (Bangkok, ending yesterday), the equal period before it, and Result's cut of
 * each (ending today − 3). Ruling R1: the cut windows are subsets of the two
 * read windows, so two facts reads serve all four. Pure.
 *
 * @module services/overview/overviewWindows
 */
import { resolveDateValue, resultWindowOf, type DateRange } from '@/services/looker/lookerDateRange';
import type { OverviewWindows } from './overviewTypes';

/** null = no bounded range: "this month" on the 1st, or a custom range the rules refuse. */
export const overviewWindowsOf = (token: string, today: string): OverviewWindows | null => {
  const r = resolveDateValue(token, today);
  if (!r || !r.range || !r.previous) return null;
  const w = resultWindowOf(r, today);
  if (!w) return null;
  const cut: DateRange | null = w.expression === 'NULL' ? null : { start: r.range.start, end: w.through };
  return {
    token: r.token,
    current: r.range,
    previous: r.previous,
    cut,
    cutPrevious: cut ? w.previous : null,
    through: w.through,
    cutApplied: w.through < r.range.end,
  };
};

/** The rows dated inside `range` (inclusive); a null range keeps none. */
export const rowsIn = <R extends { date: string }>(rows: readonly R[], range: DateRange | null): R[] =>
  (range ? rows.filter((r) => r.date >= range.start && r.date <= range.end) : []);
